import { createContext, useContext, useEffect, useRef } from 'react';
import { LayoutGrid } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Barre du bas d'un module (standard mobile Cabaza) : elle REMPLACE le dock Cabaza,
 * qui ne reste que sur les pages hors module (accueil, profil, préférences…).
 * Couleur du module pour se distinguer du dock ; seule l'icône « Modules » ramène à la
 * liste des modules, et elle disparaît en mode application autonome.
 *
 * Le layout possède la barre ; chaque module y enregistre ses onglets avec
 * `useModuleToolbar`. Sans onglets, la barre ne garde que l'icône « Modules ».
 */
export const ModuleToolbarContext = createContext(null);

// Dégradé par couleur de module, sombre pour que le texte blanc reste lisible
const GRADIENTS = {
    emerald: { bar: 'from-emerald-600 to-teal-700', active: 'text-emerald-800' },
    blue: { bar: 'from-blue-600 to-indigo-700', active: 'text-blue-800' },
    amber: { bar: 'from-amber-600 to-orange-700', active: 'text-amber-800' },
    purple: { bar: 'from-violet-600 to-purple-700', active: 'text-violet-800' },
    pink: { bar: 'from-pink-600 to-rose-700', active: 'text-pink-800' },
    teal: { bar: 'from-teal-600 to-cyan-700', active: 'text-teal-800' }
};

/**
 * Enregistre les onglets du module dans la barre du layout.
 * Passer `items = null` n'efface rien : seul le démontage (ou un autre jeu d'onglets)
 * remplace la configuration, ce qui laisse un écran enfant (détail d'un groupe) prendre
 * la barre à son parent sans conflit.
 */
export function useModuleToolbar(items, value, onChange) {
    const ctx = useContext(ModuleToolbarContext);
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const key = items ? `${items.map((i) => `${i.id}:${i.label}`).join('|')}#${value}` : '';

    useEffect(() => {
        if (!ctx || !items) return undefined;
        ctx.setConfig({ items, value, onChange: (id) => onChangeRef.current(id) });
        return () => ctx.setConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, ctx?.setConfig]);
}

const ModuleToolbar = ({ config, color = 'emerald', onModules }) => {
    const { t } = useLanguage();
    const palette = GRADIENTS[color] || GRADIENTS.emerald;
    const items = config?.items || [];
    const modulesLabel = t.menu?.modules || t.common?.modules || 'Modules';

    // Un écran plein écran (conversation ouverte) prend la place de la barre : zone de saisie en bas
    if (config?.hidden) return null;

    return (
        <nav
            aria-label={modulesLabel}
            className={`sm:hidden fixed left-4 right-4 bottom-4 z-40 h-16 rounded-3xl bg-gradient-to-r ${palette.bar} shadow-xl shadow-black/20 flex items-center gap-1.5 px-2`}
        >
            {onModules && (
                <button
                    type="button"
                    onClick={onModules}
                    aria-label={modulesLabel}
                    className="w-12 h-12 rounded-2xl bg-white/20 text-white flex items-center justify-center shrink-0 active:bg-white/30"
                >
                    <LayoutGrid className="w-5 h-5" strokeWidth={1.75} />
                </button>
            )}

            {items.length > 0 ? (
                <div role="tablist" className="flex-1 min-w-0 flex gap-1">
                    {items.map((item) => {
                        const isActive = item.id === config.value;
                        return (
                            <button
                                key={item.id}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                onClick={() => config.onChange(item.id)}
                                className={`flex-1 min-w-0 h-12 px-1 rounded-2xl text-[13px] font-semibold truncate transition-colors ${
                                    isActive ? `bg-white ${palette.active} shadow-sm` : 'text-white/90 active:bg-white/15'
                                }`}
                            >
                                {item.label}
                            </button>
                        );
                    })}
                </div>
            ) : (
                <span className="flex-1 px-2 text-sm font-semibold text-white/90 truncate">{modulesLabel}</span>
            )}
        </nav>
    );
};

export default ModuleToolbar;
